import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Upload,
  FileText,
  Loader2,
  Trash2,
  Eye,
  EyeOff,
  ShieldCheck,
  ShieldAlert,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ConfidentialAssetFooter } from "@/components/site/ConfidentialAssetFooter";
import {
  adminListCompanyAttachments,
  recordCompanyAttachment,
  toggleCompanyAttachment,
  deleteCompanyAttachment,
  adminGetCompanyAttachmentUrl,
} from "@/lib/api/company-assets.functions";
import {
  adminRunCompanyAssetsQa,
  adminVerifyPdfUpload,
} from "@/lib/api/company-assets-qa.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/company-assets",
)({
  head: () => ({
    meta: [
      { title: "Company assets | Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CompanyAssetsAdmin,
});

const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "moi", label: "Memorandum of Incorporation (MOI)" },
  { value: "constitution", label: "Company Constitution" },
  { value: "profile", label: "Company Profile" },
  { value: "management_plan", label: "Management Plan & System" },
  { value: "bbbee", label: "B-BBEE Certificate" },
  { value: "tax_clearance", label: "Tax Clearance" },
  { value: "other", label: "Other" },
];

const MAX_BYTES = 25 * 1024 * 1024;

function bytesPretty(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function bumpVersion(v: string | null | undefined) {
  const m = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?$/i.exec((v ?? "").trim());
  if (!m) return "v1.0";
  const major = Number(m[1] ?? 1);
  const minor = Number(m[2] ?? 0) + 1;
  return `v${major}.${minor}`;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode(
      ...(buf.subarray(i, i + chunk) as unknown as number[]),
    );
  }
  return btoa(binary);
}

function CompanyAssetsAdmin() {
  const qc = useQueryClient();
  const fetchList = useServerFn(adminListCompanyAttachments);
  const saveMeta = useServerFn(recordCompanyAttachment);
  const toggle = useServerFn(toggleCompanyAttachment);
  const remove = useServerFn(deleteCompanyAttachment);
  const runQa = useServerFn(adminRunCompanyAssetsQa);
  const verifyPdf = useServerFn(adminVerifyPdfUpload);
  const getSignedUrl = useServerFn(adminGetCompanyAttachmentUrl);
  const [busyAssetId, setBusyAssetId] = useState<string | null>(null);

  async function onPreview(id: string) {
    try {
      setBusyAssetId(id);
      const res = await getSignedUrl({ data: { id, download: false } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not open preview");
    } finally {
      setBusyAssetId(null);
    }
  }

  async function onDownload(id: string) {
    try {
      setBusyAssetId(id);
      const res = await getSignedUrl({ data: { id, download: true } });
      const a = document.createElement("a");
      a.href = res.url;
      a.download = res.filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      toast.error(e?.message ?? "Could not download asset");
    } finally {
      setBusyAssetId(null);
    }
  }

  const { data } = useQuery({
    queryKey: ["admin-company-attachments"],
    queryFn: () => fetchList(),
  });

  const [kind, setKind] = useState("moi");
  const [label, setLabel] = useState("");
  const [version, setVersion] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [verifyState, setVerifyState] = useState<
    | null
    | {
        passes: boolean;
        page_count: number;
        pages_with_brand: number;
        pages_with_image: number;
        issues: string[];
      }
  >(null);
  const [uploading, setUploading] = useState(false);

  const rows = data?.attachments ?? [];
  const suggestedVersion = useMemo(() => {
    const same = rows.filter((r: any) => r.kind === kind);
    if (same.length === 0) return "v1.0";
    return bumpVersion(same[0].version);
  }, [rows, kind]);

  function onPickFile(f: File | null) {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setVerifyState(null);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
  }

  const qaMut = useMutation({
    mutationFn: () => runQa(),
  });

  const verifyMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Pick a PDF first");
      const base64 = await fileToBase64(file);
      return verifyPdf({ data: { filename: file.name, base64 } });
    },
    onSuccess: (res: any) => {
      setVerifyState({
        passes: !!res.passes,
        page_count: res.page_count ?? 0,
        pages_with_brand: res.pages_with_brand ?? 0,
        pages_with_image: res.pages_with_image ?? 0,
        issues: res.issues ?? [],
      });
      if (res.passes) toast.success("Brand check passed");
      else toast.warning("Brand check failed — review issues below");
    },
    onError: (e: any) => toast.error("Verify failed", { description: e?.message }),
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file.");
    if (file.size > MAX_BYTES)
      return toast.error("File too large", { description: "Max 25 MB." });
    if (!verifyState?.passes) {
      if (
        !confirm(
          "This PDF has not passed brand verification (brand name + logo on every page). Upload anyway?",
        )
      )
        return;
    }
    const finalLabel =
      label.trim() ||
      KIND_OPTIONS.find((k) => k.value === kind)?.label ||
      "Company document";
    const finalVersion = (version.trim() || suggestedVersion).toLowerCase();

    setUploading(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
      const path = `${kind}/${finalVersion}-${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage
        .from("company-assets")
        .upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type || "application/pdf",
        });
      if (upErr) throw upErr;
      await saveMeta({
        data: {
          kind,
          label: finalLabel,
          storage_path: path,
          original_filename: file.name,
          size_bytes: file.size,
          mime_type: file.type || "application/pdf",
          version: finalVersion,
        },
      });
      toast.success(`Uploaded ${finalVersion}`);
      setLabel("");
      setVersion("");
      onPickFile(null);
      (document.getElementById("ca-file") as HTMLInputElement | null) &&
        ((document.getElementById("ca-file") as HTMLInputElement).value = "");
      qc.invalidateQueries({ queryKey: ["admin-company-attachments"] });
    } catch (err: any) {
      toast.error("Upload failed", { description: err?.message ?? String(err) });
    } finally {
      setUploading(false);
    }
  }

  async function onToggle(id: string, is_active: boolean) {
    try {
      await toggle({ data: { id, is_active: !is_active } });
      qc.invalidateQueries({ queryKey: ["admin-company-attachments"] });
    } catch (err: any) {
      toast.error("Update failed", { description: err?.message });
    }
  }

  async function onDelete(id: string) {
    if (!confirm("Delete this file? It will no longer attach to new PDFs."))
      return;
    try {
      await remove({ data: { id } });
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-company-attachments"] });
    } catch (err: any) {
      toast.error("Delete failed", { description: err?.message });
    }
  }

  const qaReport: any[] = (qaMut.data as any)?.report ?? [];
  const qaSummary: any = (qaMut.data as any)?.summary;

  return (
    <div className="space-y-8">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">
          Reusable company files
        </p>
        <h2 className="mt-1 font-display text-2xl text-navy">Company assets</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Upload, version, preview and brand-check the MOI, constitution,
          profile and management plan. Only PDFs that show{" "}
          <strong>Nkadimeng Danny Legal and Compliance Support</strong> and the
          company logo on every page should be published.
        </p>
      </header>

      {/* Upload form */}
      <form
        onSubmit={onSubmit}
        className="grid gap-4 rounded-2xl border border-border bg-card p-6 shadow-soft md:grid-cols-2"
      >
        <div>
          <Label>Category</Label>
          <Select
            value={kind}
            onValueChange={(v) => {
              setKind(v);
              setVersion("");
            }}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ca-label">Label (shown to clients)</Label>
          <Input
            id="ca-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. MOI — Nkadimeng Danny Legal and Compliance Support"
            maxLength={120}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="ca-version">
            Version{" "}
            <span className="text-xs text-muted-foreground">
              (auto: {suggestedVersion})
            </span>
          </Label>
          <Input
            id="ca-version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder={suggestedVersion}
            maxLength={20}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="ca-file">PDF file (max 25 MB)</Label>
          <Input
            id="ca-file"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            className="mt-1"
            required
          />
          {file && (
            <p className="mt-1 text-xs text-muted-foreground">
              {file.name} · {bytesPretty(file.size)}
            </p>
          )}
        </div>

        {previewUrl && (
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Live preview
            </Label>
            <iframe
              title="PDF preview"
              src={previewUrl}
              className="mt-1 h-[420px] w-full rounded-lg border border-border bg-muted"
            />
          </div>
        )}

        {file && (
          <div className="md:col-span-2 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={verifyMut.isPending}
              onClick={() => verifyMut.mutate()}
            >
              {verifyMut.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking…
                </>
              ) : (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4" /> Verify branding
                </>
              )}
            </Button>
            {verifyState && (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${
                  verifyState.passes
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {verifyState.passes ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <ShieldAlert className="h-3.5 w-3.5" />
                )}
                {verifyState.passes ? "Passes" : "Issues found"} · {verifyState.page_count}{" "}
                pages · brand {verifyState.pages_with_brand}/{verifyState.page_count} · logo{" "}
                {verifyState.pages_with_image}/{verifyState.page_count}
              </span>
            )}
          </div>
        )}

        {verifyState && verifyState.issues.length > 0 && (
          <ul className="md:col-span-2 list-disc space-y-1 rounded-lg bg-amber-50 px-6 py-3 text-xs text-amber-900">
            {verifyState.issues.map((i, idx) => (
              <li key={idx}>{i}</li>
            ))}
          </ul>
        )}

        <div className="md:col-span-2">
          <Button
            type="submit"
            disabled={uploading || !file}
            className="bg-navy hover:bg-navy/90"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" /> Upload asset
              </>
            )}
          </Button>
        </div>
      </form>

      {/* QA panel */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg text-navy">Automated QA</h3>
            <p className="text-sm text-muted-foreground">
              Downloads every active and inactive asset and checks: PDF byte
              size matches the database, page count, brand name on every page,
              and logo image on every page.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => qaMut.mutate()}
            disabled={qaMut.isPending}
          >
            {qaMut.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…
              </>
            ) : (
              <>
                <PlayCircle className="mr-2 h-4 w-4" /> Run QA
              </>
            )}
          </Button>
        </div>

        {qaSummary && (
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <span className="rounded-full bg-muted px-3 py-1 text-navy">
              Checked {new Date(qaSummary.checked_at).toLocaleString("en-ZA")}
            </span>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-700">
              Passing: {qaSummary.passing}/{qaSummary.total}
            </span>
            <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">
              Failing: {qaSummary.failing}
            </span>
          </div>
        )}

        {qaReport.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[800px] text-left text-xs">
              <thead className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Asset</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Size (DB / actual)</th>
                  <th className="px-3 py-2">Pages</th>
                  <th className="px-3 py-2">Brand</th>
                  <th className="px-3 py-2">Logo</th>
                  <th className="px-3 py-2">DB updated</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {qaReport.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy">{r.label}</div>
                      <div className="text-muted-foreground">{r.kind}</div>
                    </td>
                    <td className="px-3 py-2">{r.version}</td>
                    <td className="px-3 py-2">
                      {bytesPretty(r.db.size_bytes)} /{" "}
                      {r.actual ? bytesPretty(r.actual.size_bytes) : "—"}
                    </td>
                    <td className="px-3 py-2">{r.actual?.page_count ?? "—"}</td>
                    <td className="px-3 py-2">
                      {r.actual
                        ? `${r.actual.pages_with_brand}/${r.actual.page_count}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {r.actual
                        ? `${r.actual.pages_with_image}/${r.actual.page_count}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {new Date(r.db.updated_at).toLocaleString("en-ZA")}
                    </td>
                    <td className="px-3 py-2">
                      {r.ok ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> OK
                        </span>
                      ) : (
                        <span
                          title={r.issues.join("\n")}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800"
                        >
                          <XCircle className="h-3 w-3" /> {r.issues.length} issue
                          {r.issues.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Uploaded asset list */}
      <section>
        <h3 className="font-display text-lg text-navy">Uploaded assets</h3>
        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            No assets yet. Upload your MOI or constitution to make them
            available.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-border rounded-xl border border-border bg-card">
            {rows.map((r: any) => (
              <li key={r.id} className="flex items-start gap-3 p-4">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-mono text-navy">
                      {r.kind}
                    </span>
                    <span className="rounded bg-navy/10 px-1.5 py-0.5 text-[11px] font-mono text-navy">
                      {r.version}
                    </span>
                    {!r.is_active && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-sm font-medium text-navy">
                    {r.label}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {r.original_filename} · {bytesPretty(Number(r.size_bytes))} ·
                    updated {new Date(r.updated_at).toLocaleString("en-ZA")}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onPreview(r.id)}
                    disabled={busyAssetId === r.id}
                  >
                    {busyAssetId === r.id ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Eye className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Preview
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={() => onDownload(r.id)}
                    disabled={busyAssetId === r.id}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onToggle(r.id, r.is_active)}
                  >
                    {r.is_active ? (
                      <>
                        <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Hide
                      </>
                    ) : (
                      <>
                        <Eye className="mr-1.5 h-3.5 w-3.5" /> Activate
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(r.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfidentialAssetFooter />
    </div>
  );
}

